/**
 * ===========================================================================
 * INSIGHTS → THE ATTENTION BUDGET. Two routes, one number, and its meaning.
 * ===========================================================================
 *
 * Founder closeout decision 3 of 2026-09-08:
 *
 *   "Any tenant-configurable value must have EXPLICIT SEMANTICS, INCLUDING ITS
 *    UNIT, TIME BASIS/CADENCE, AND BEHAVIOR."
 *
 * ---------------------------------------------------------------------------
 * THE SEMANTICS TRAVEL WITH THE VALUE, AND THAT IS THE WHOLE DESIGN OF THIS
 * MODULE
 * ---------------------------------------------------------------------------
 * A client that knew the number was "interruptions per person per day" would
 * be a SECOND statement of the semantics — one that agrees today and drifts
 * silently, which is precisely the defect the decision names. So this module
 * declares the SHAPE of the answer and none of its CONTENT: `semantics` and
 * `effectiveDescription` arrive from the server already composed, and a screen
 * renders them rather than writing a sentence of its own.
 *
 * The same rule governs `refusalCodes`. The whole closed vocabulary comes down
 * on every response, exactly as `PUT /location/policy` sends its `codes`, so a
 * client that meets an unfamiliar code can tell "a code I do not handle" from
 * "a code that does not exist" — and can never hard-code the list.
 *
 * ---------------------------------------------------------------------------
 * FOUR STATES, KEPT APART
 * ---------------------------------------------------------------------------
 *   undeclared + no provenance   ABSENT. Nobody ever set one. No budget is
 *                                applied; the organization is as loud as it
 *                                was before this surface existed.
 *   undeclared + provenance      WITHDRAWN. Somebody set one and then turned it
 *                                off. Still no budget applied — and still a
 *                                different fact, because a deferral recorded
 *                                last Tuesday cites a configuration that has to
 *                                stay readable.
 *   org_rule, clamped: null      In force, exactly as declared.
 *   org_rule, 'delivery_ceiling' In force, but the PLATFORM cut it down. That
 *                                is a delivery safety control and not an
 *                                opinion about attention, and it says so.
 *
 * A `basis` of `undeclared` carries NO NUMBER AT ALL. It is not `0`, and the
 * type makes reading it as one impossible: there is no field to read.
 */
import { get, put } from './transport.js';
/**
 * `GET /api/insights/attention-budget` → the budget, its meaning, and what is
 * currently deferred (needs `insights:attention_budget`).
 */
export async function read(options) {
    return get('/api/insights/attention-budget', options);
}
/**
 * `PUT /api/insights/attention-budget` → the budget after the change (needs
 * `insights:attention_budget`).
 *
 * `cap: null` WITHDRAWS. It does not delete: the record of what was in force
 * survives, because a deferral recorded under it must stay explicable.
 *
 * A refusal arrives as a thrown `ApiError` with a `400` and a body carrying a
 * stable `code` — it is not returned, and it is NOT A RETRY. The server refuses
 * zero by name rather than storing it, because a budget of zero is an off
 * switch wearing a budget's clothes.
 */
export async function declare(input, options) {
    return put('/api/insights/attention-budget', input, options);
}
//# sourceMappingURL=attentionBudget.js.map